/**
 * Google Analytics 4, all custom events for Professya.
 * Only fires if user has accepted analytics cookies.
 */

const CONSENT_KEY = 'professya_cookie_consent';

function canTrack(): boolean {
  const stored = localStorage.getItem(CONSENT_KEY);
  return stored === 'accepted' || stored === 'all';
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function gtag(...args: unknown[]): void {
  if (!canTrack()) return;
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer ?? [];
  (window.dataLayer as unknown[]).push(args);
  window.gtag?.(...args);
}

export function initGA4(measurementId: string): void {
  if (!canTrack() || typeof window === 'undefined') return;
  if (!measurementId) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function (...args: unknown[]) {
    (window.dataLayer as unknown[]).push(args);
  };
  gtag('js', new Date());
  gtag('config', measurementId, { send_page_view: false });
}

export function trackPageView(path: string, title: string): void {
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  if (!id) return;
  gtag('event', 'page_view', { page_path: path, page_title: title, send_to: id });
}

// ─── Auth events ──────────────────────────────────────────────────────────────

export function trackRegistrationStart(): void {
  gtag('event', 'registration_start');
}

export function trackRegistrationComplete(): void {
  gtag('event', 'registration_complete');
}

// ─── Onboarding events ────────────────────────────────────────────────────────

export function trackOnboardingStep(step: 1 | 2 | 3 | 4): void {
  gtag('event', 'onboarding_step', { step });
}

export function trackBirthDataAdded(): void {
  gtag('event', 'birth_data_added');
}

// ─── Reading events ───────────────────────────────────────────────────────────

export function trackReadingStarted(feature: string, period?: string): void {
  gtag('event', 'reading_started', { feature, period: period ?? null });
}

export function trackReadingCompleted(feature: string, durationSec: number): void {
  gtag('event', 'reading_completed', { feature, duration_sec: durationSec });
}

export function trackReadingShared(feature: string, platform: 'whatsapp' | 'copy'): void {
  gtag('event', 'reading_shared', { feature, platform });
}

export function trackReadingFeedback(feature: string, value: 1 | -1): void {
  gtag('event', 'reading_feedback', { feature, value });
}

// ─── Checkout events ──────────────────────────────────────────────────────────

export function trackCheckoutStarted(product: string, value: number): void {
  gtag('event', 'checkout_started', { product, value });
}

export function trackCheckoutCompleted(product: string, value: number): void {
  gtag('event', 'checkout_completed', { product, value });
}

export function trackCheckoutAbandoned(product: string): void {
  gtag('event', 'checkout_abandoned', { product });
}

// ─── Credit events ────────────────────────────────────────────────────────────

export function trackCreditDepleted(creditType: string): void {
  gtag('event', 'credit_depleted', { credit_type: creditType });
}

// ─── Upsell events ────────────────────────────────────────────────────────────

export function trackUpsellShown(product: string, context: string): void {
  gtag('event', 'upsell_shown', { product, context });
}

export function trackUpsellConverted(product: string): void {
  gtag('event', 'upsell_converted', { product });
}
